import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  OPTIONAL_NUMERIC_TEXT_INPUT,
  OPTIONAL_TEXT_INPUT,
} from "./optionalInputs";

export const warehouseCustomerAccountInputSchema = z.object({
  dataSourceLink: z.object({
    id: z.string().uuid(),
    dataSourceId: z.string().uuid().optional(),
  }),
  metadataId: z.string().min(1),
  changeOperation: z.enum(["insert", "update", "delete"]),
  changeLsn: z.string().regex(/^\d+$/),
  warehouseCode: OPTIONAL_TEXT_INPUT.describe(
    "The branch the source location stands for, from a location-to-branch mapping. Empty when the location is not a branch.",
  ),
  divisionCode: OPTIONAL_TEXT_INPUT.describe(
    "The code the app knows the branch's division by (its DataSource title), the same code customer-record links resolve to.",
  ),
  customerNumber: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
    "The customer number the ERP keeps for the branch itself.",
  ),
});
export type WarehouseCustomerAccountInput = z.infer<
  typeof warehouseCustomerAccountInputSchema
>;

export const upsertWarehouseCustomerAccountStep = defineStepConfig({
  type: "upsert_warehouse_customer_account",
  name: "Upsert Warehouse Customer Account",
  description:
    "Maintains the customer number each branch has in the ERP, so customer-record feeds can find the branch, including CDC deletion and ordering.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: warehouseCustomerAccountInputSchema,
  outputSchema: z.object({
    written: z.boolean(),
    deleted: z.boolean(),
    skipped: z.boolean(),
    reason: z
      .enum([
        "not_a_branch",
        "missing_division",
        "missing_customer_number",
        "unresolved_warehouse",
        "stale_event",
        "not_found",
      ])
      .nullable(),
  }),
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
    writeOriginSystem: z.string().min(1),
    warehouseOriginSystem: z
      .string()
      .optional()
      .describe("Warehouse lookup origin system."),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
